import {
  InlandMarineTypes,
  FarmRiskType,
  LiabilityType,
  lookupsJson,
  standardizedCancelNonRenewalReasonCodes
} from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';

import farmUWQuestions from './oakwood/farm-uw-questions';

const allConstructionTypeIds = lookupsJson.farm.agriculturalConstructionType.map((ct) => ct.id);

// Oakwood's per-state farm config. Kept as a named object rather than inlined under `IN` so a
// second state can be added by reference instead of by copying ~100 lines. Consumers read it as
// `appConfig.farm?.[state]?...`, so a state absent from `farm` below silently resolves to
// undefined and the staff form loses its hidden-coverage and custom-component rules.
const oakwoodFarmStateConfig = {
  liabilityExtensions: {
    rented: {
      [LiabilityType.Personal]: { locations: true, residences: true, structures: true },
      [LiabilityType.Commercial]: { locations: true, residences: true, structures: true }
    },
    occupied: {
      [LiabilityType.Personal]: { locations: true, residences: true },
      [LiabilityType.Commercial]: { locations: true, residences: true }
    },
    assets: {
      [LiabilityType.Personal]: { atv: true, golfCart: true, unmannedAircraft: true, boat: true },
      [LiabilityType.Commercial]: { atv: true, golfCart: true, unmannedAircraft: true, boat: true }
    }
  },
  risks: {
    [FarmRiskType.Fo1]: {
      hiddenCoverages: [],
      customFormComponentPaths: {}
    },
    [FarmRiskType.Fo2]: {
      // Inland Flood (#32440) is currently enabled only for CFM MO; hidden here so the shared
      // FO2 render doesn't surface it for Oakwood.
      hiddenCoverages: ['inlandFlood'],
      customFormComponentPaths: {}
    },
    [FarmRiskType.Fo3]: {
      hiddenCoverages: ['inlandFlood'],
      customFormComponentPaths: {},
      allowedOptions: {
        inlandMarineItemTypes: [
          InlandMarineTypes.BoatDock,
          InlandMarineTypes.Boats,
          InlandMarineTypes.CoinCollection,
          InlandMarineTypes.FineArts,
          InlandMarineTypes.Furs,
          InlandMarineTypes.GolfEquipment,
          InlandMarineTypes.GolfCarts,
          InlandMarineTypes.Guns,
          InlandMarineTypes.Jewelry,
          InlandMarineTypes.LawnMowers,
          InlandMarineTypes.MiscellaneousProperty,
          InlandMarineTypes.MusicalInstruments,
          InlandMarineTypes.PersonalTools,
          InlandMarineTypes.Silverware,
          InlandMarineTypes.SportingEquipment,
          InlandMarineTypes.Tack,
          InlandMarineTypes.UnmannedAircraft
        ]
      }
    },
    [FarmRiskType.Fo4]: {
      hiddenCoverages: ['inlandFlood'],
      customFormComponentPaths: {},
      allowedOptions: {
        inlandMarineItemTypes: [
          InlandMarineTypes.BoatDock,
          InlandMarineTypes.Boats,
          InlandMarineTypes.CoinCollection,
          InlandMarineTypes.FineArts,
          InlandMarineTypes.Furs,
          InlandMarineTypes.GolfEquipment,
          InlandMarineTypes.GolfCarts,
          InlandMarineTypes.Guns,
          InlandMarineTypes.Jewelry,
          InlandMarineTypes.LawnMowers,
          InlandMarineTypes.MiscellaneousProperty,
          InlandMarineTypes.MusicalInstruments,
          InlandMarineTypes.PersonalTools,
          InlandMarineTypes.Silverware,
          InlandMarineTypes.SportingEquipment,
          InlandMarineTypes.Tack,
          InlandMarineTypes.UnmannedAircraft
        ]
      }
    },
    [FarmRiskType.Fo6Scheduled]: {
      hiddenCoverages: [],
      customFormComponentPaths: {}
    },
    [FarmRiskType.Fo6Unscheduled]: {
      hiddenCoverages: [],
      customFormComponentPaths: {}
    },
    [FarmRiskType.Fo6Structures]: {
      // Coverage E EQ deductible was introduced for CFM MO (issue #32439); Oakwood does not
      // offer earthquake coverage, so the new BrandFormField in FO6.jsx is hidden here.
      hiddenCoverages: ['deductibleEarthquake'],
      allowedOptions: {
        constructionTypes: allConstructionTypeIds.filter((id) => id !== 'MH')
      },
      businessRules: {
        buildingClass1MaxAgeYears: 25
      },
      customFormComponentPaths: {}
    }
  }
};

export default {
  spinner: 'ember',
  // How long the newer-offer interstitial dwells before it redirects (#30994). It is the only
  // window staff have to reach "Keep version" and stay on the offer they opened, so Oakwood keeps the original 5s.
  offerRedirectInterstitialMs: 5000,
  // Issue #33591: standardized cancellation / non-renewal reasons shared across the mutuals.
  // The ordered ids turn on the matching `optIn` entries from the `cancelReason` superset.
  // Issue #35987: NACP ('Policy Not Accepted') brings the mutuals to parity with Branch/BIX. It is a
  // plain (non-optIn) cancellation reason in the superset, so it resolves into the CANCEL dropdown
  // (not non-renewal). Oakwood has no CNRW rewrite entry, so NACP is appended directly.
  cancelNonRenewalReasons: [...standardizedCancelNonRenewalReasonCodes, 'NACP'],
  featureFlags: {
    // Incidents tab lists 3-5 year old home losses from homeClaimsForUW as not rated (#36608).
    // Branch home raters read only homeClaimDates; mutual raters can price on homeClaimsForUW.
    showNotRatedHomeClaims: false,
    // Opening a rewritten (CNRW) original asks whether to go to the rewritten policy (#36661).
    policyRewrittenRedirect: true,
    // A customer can hold a FarmHome (Z) quote on one property and a FarmRental (Y) quote on
    // another. Deduping offer search on `webUserId` hid one of them, so agents kept opening and
    // editing the wrong quote (#34790). Dedupe on `offerSearchDedupeKey` instead — still one row
    // per quote, but per product set rather than per customer.
    //
    // OFF until this brand's offer index is reindexed. The deploy's sync-meili-settings PATCHes
    // the filterable attribute and returns immediately without waiting, and until that rebuild
    // finishes the old settings are still live — so a `distinct` on the new attribute ERRORS, not
    // degrades. Once reindexed the documents carry the key and this flips to true; flipping early
    // breaks farm offer search outright.
    offerSearchDedupesByProductSet: false,
    // Underwriters may override the system-pulled insurance score with a manually entered
    // value on the applicant/driver cards; the rater uses the manual score. CFM and Oakwood
    // rate on credit; Forreston does not, so the control is hidden there. (#34377)
    insuranceScoreOverride: true,
    // Underwriter protection-class (fpc) override, enabled for the mutual brands. (#35212)
    protectionClassOverride: true,
    // Descope-authority override that LOCKS Coverage A & Coverage E limits with a required internal
    // reason (#35617). On for the mutual brands; off for Branch. See branch.js for the full note.
    coverageLimitOverride: true,
    // Parity with the other mutuals: the applicant's insurance score is underwriting information,
    // so agency users do not see it on any person card. Internal staff still do. (#35046)
    hideInsuranceScoreFromAgents: true,
    insuranceScoreHelp: false,
    applicantDateOfBirthAtNewOffer: true,
    // Letter of Experience generator is Branch-only for now (#29700) — off until the mutual
    // letter format is confirmed.
    generateLoe: false,
    // Underwriters (CanDeleteInternalDocuments) may delete offer Supporting Documents; mutual-only,
    // off on Branch. (#34202)
    deleteSupportingDocuments: true,
    // "Suppress communications" on the endorsement confirm, internal staff only (#37246).
    suppressEndorsementCommunications: true,
    // Staff "Send Invoice" billing action; mutual-only, off on Branch. (#34203, #34419)
    sendInvoice: true,
    // Agent payment presets + staff sweep charges for a requesting agent (#36683). CFM/Forreston only.
    agentPaymentOptions: false,
    // Billing holds stop only automated billing and cancellation; manual payments always go through (#37417).
    manualPaymentsThroughBillingHold: true,
    // Staff invoice history on the Customer page; mutual-only, off on Branch. (#36679)
    invoiceHistory: true,
    // Offer search results redesign (#36395): Branch only for v1.
    offerSearchRedesign: false,
    // Farm agents may have a personal mailing address in a state they don't write in;
    // the licensed-state check is enforced per farm location instead. (Issue #30876)
    enforceLicensedStateOnNamedInsured: false,
    // Show the agency Portal (Notion pages) to every user on this mutual, not just
    // agents/underwriters. (#31094, #33024)
    agencyPortalVisibleToAllUsers: true,
    // Agency sweep accounts (#33155): independent agents can pay from their agency's
    // Stripe account. On in non-prod and now in production too (#35085).
    sweepAccounts: { enabledInNonProd: true, enabledInProd: true },
    // Agency Reports (#36219): Branch-only until this brand has its own report bucket.
    agencyReports: { enabled: false, visibleToAgencies: false },
    // Underwriter MicroBilt-bypass on ACH adds (#33938): mutual-only, on in every env.
    microBiltVerificationBypass: true,
    // "Collect payment information later" at bind (#33891): mutual-only.
    collectPaymentLater: true,
    // Policy drafting (working drafts / draft-and-keep-changing endorsement flow).
    // Consumed via core/helpers/policy-drafting.js.
    policyDrafts: true
  },
  draftablePolicyTypes: [policyTypes.Farm, policyTypes.FarmHome, policyTypes.FarmRental],
  discounts: {
    hasFutureEffectiveDateDiscount: false,
    hasAdvanceQuoteDiscount: false,
    hasPaperlessDiscount: false
  },
  farm: {
    // Oakwood farm UW questions. Currently identical to the pre-brand-split base set; diverges as
    // Oakwood authors its own question tree.
    uwQuestions: farmUWQuestions,
    IN: oakwoodFarmStateConfig
  },
  agencyPortalRoutes: [
    {
      route: 'updates',
      src: 'https://portal.ourbranch.com/ebd/41f113b696aa83b283f001d054b1fa8b',
      title: 'News and Updates',
      icon: 'portal'
    },
    {
      route: 'reports',
      src: 'https://portal.ourbranch.com/ebd/3c3113b696aa830f8bdf812a54c612af',
      title: 'Reports & Commissions',
      icon: 'pieChart'
    },
    {
      route: 'underwriting',
      src: 'https://portal.ourbranch.com/ebd/01d113b696aa82139e2401c1e3c5fdfa',
      title: 'Underwriting',
      icon: 'pen'
    },
    {
      route: 'tools',
      src: 'https://portal.ourbranch.com/ebd/aaf113b696aa833a822f81bb842ae80e',
      title: 'Tools & Resources',
      icon: 'tool'
    },
    {
      route: 'training',
      src: 'https://portal.ourbranch.com/ebd/f61113b696aa830ea5d001d2e6c01f98',
      title: 'Training',
      icon: 'puzzlePiece'
    },
    {
      route: 'claims',
      src: 'https://portal.ourbranch.com/ebd/4c6113b696aa83a49a41813fa97c3ac0',
      title: 'Claims',
      icon: 'claim'
    },
    {
      route: 'contact-us',
      src: 'https://portal.ourbranch.com/ebd/a61113b696aa834c8bf08142cfa2284b',
      title: 'Contact',
      icon: 'contact'
    }
  ]
};
